<template>
  <div>
    <div>
      <div class="mb-3" :class="{ disabled: loading}">
        <DateRangeSelector
          v-model:date-from="selectorRange.from"
          v-model:date-to="selectorRange.to"
          v-model:mode="selectorMode"
          :show-month="true"
          :range-limit="40"
          default-range="last30Days"
          @update="updateDates"
        />
      </div>
      <div class="mx-0h mb-3" :class="{ disabled: loading}">
        <div class="flex align-center">
          <CheckboxComponent
            v-for="metric in requestedMetrics"
            :key="metric.key"
            v-model="metric.value"
            :label="metric.label"
          />
          <div class="spacer" />
          <BtnComponent v-if="!isCheckedAll" outline @click="toggleCheckAll">
            {{ $t('unit.protocols.checkAll') }}
          </BtnComponent>
          <BtnComponent v-else outline @click="toggleCheckAll">
            {{ $t('unit.protocols.uncheckAll') }}
          </BtnComponent>
        </div>
      </div>
      <div :class="{ disabled: loading}">
        <div class="row mb-3">
          <div class="col-12 col-tablet-3">
            <SelectComponent
              v-model="requestedAverage"
              :options="averageOptions"
              :label="$t('unit.protocols.average')"
            />
          </div>
          <div class="col-12 col-tablet-9">
            <InputComponent
              v-model="fileName"
              :label="$t('unit.protocols.fileName')"
            />
          </div>
        </div>
      </div>
      <div class="flex align-center">
        <div>
          {{ $t('unit.protocols.messageLineOne') }} <br>
          {{ $t('unit.protocols.messageLineTwo') }}
        </div>
        <div class="spacer" />
        <BtnComponent
          primary
          large
          :disabled="!dateFrom || !dateTo || loading || isCheckedNone"
          :loading="loading"
          @click="sendExportRequest"
        >
          {{ $t('unit.protocols.download') }}
        </BtnComponent>
      </div>
      <div v-if="!dateFrom || !dateTo" class="text-right mt-0h" style="font-size: 0.8125rem">
        <IconComponent icon="aip-alert-triangle" size="1rem" style="color: yellow" />
        {{ $t('unit.protocols.selectRange') }}
      </div>
    </div>
  </div>
</template>

<script>
import CheckboxComponent from '@acs/components-library/src/components/common/Checkbox.vue';
import InputComponent from '@acs/components-library/src/components/common/Input.vue';
import SelectComponent from '@acs/components-library/src/components/common/Select.vue';
import BtnComponent from '@acs/components-library/src/components/common/Btn.vue';
import IconComponent from '@acs/components-library/src/components/common/Icon.vue';

import DateRangeSelector from '@acs/components-library/src/components/deviceDetail/protocols/DateRangeSelector.vue';

import { MetricQuery } from '@acs/components-library/src/gql/live/UnitDetailProtocols.js';
// import { isDemo } from '@acs/components-library/src/demo.js';

const { VITE_GRAPHQL_REST_URL } = import.meta.env;
import { createHeaders } from '@acs/components-library/src/fetch.js';
import { useToastStore } from '../store/toastStore.js';

export default {
  components: {
    BtnComponent,
    CheckboxComponent,
    InputComponent,
    SelectComponent,
    DateRangeSelector,
    IconComponent,
  },
  props: {
    sn: {
      type: String,
      required: true,
    },
    unpaired: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  data() {
    return {
      loading: false,
      selectedValue: false,
      fileName: '',
      statusValues: [],
      requestedMetrics: [],
      requestedAverage: 'hour',
      averageOptions: [
        { value: 'hour', text: this.$t('unit.protocols.hourly') },
        { value: 'day', text: this.$t('unit.protocols.daily') },
      ],
      selectorMode: 'range', // 'day' | 'range';
      selectorRange: {
        from: null,
        to: null,
      },
      dateFrom: null,
      dateTo: null,
    };
  },
  apollo: {
    unitBySerialNumber: {
      query: MetricQuery,
      variables() {
        return { sn: this.sn };
      },
      skip() {
        return this.unpaired || !this.sn;
      },
    },
  },
  computed: {
    unit() {
      return this.unitBySerialNumber;
    },
    dateFromObject() {
      if (!this.dateFrom) {
        return null;
      }
      return new Date(this.dateFrom);
    },
    dateToObject() {
      if (!this.dateTo) {
        return null;
      }
      return new Date(this.dateTo);
    },
    isCheckedAll() {
      return this.requestedMetrics.every((sv) => sv.value);
    },
    isCheckedNone() {
      return this.requestedMetrics.filter((sv) => sv.value).length === 0;
    },
  },
  watch: {
    dateFrom() {
      this.changeNameOfExport();
    },
    dateTo() {
      this.changeNameOfExport();
    },
    requestedAverage() {
      this.changeNameOfExport();
    },
    requestedMetrics: {
      handler: function () {
        this.changeNameOfExport();
      },
      deep: true,
    },
    unit(unit) {
      this.fileName = unit.name;
      if (!unit) {
        return;
      }
      if (this.unit?.unitModel?.availableProtocolMetrics.length) {
        this.requestedMetrics = this.unit.unitModel.availableProtocolMetrics.map((metric) => {
          return {
            key: metric.type,
            value: false,
            label: metric.label,
          };
        });
      }
    },
  },
  methods: {
    async updateDates() {
      await this.$nextTick();
      if (this.selectorRange.to) {
        this.dateFrom = new Date(this.selectorRange.from);
        this.dateTo = new Date(this.selectorRange.to);
      }
    },
    toggleCheckAll() {
      if (this.isCheckedAll) {
        this.requestedMetrics.forEach((sv) => sv.value = false);
      } else {
        this.requestedMetrics.forEach((sv) => sv.value = true);
      }
    },
    changeNameOfExport() {
      if (!this.unit || !this.dateFrom || !this.dateTo) {
        return;
      }
      const values = this.requestedMetrics.reduce((acc, sv) => {
        if (sv.value) {
          acc.push(sv.label);
        }
        return acc;
      }, []);
      let valuesString = values.join(', ');
      if (values.length) {
        valuesString = ` - ${valuesString} - `;
      }
      const average = this.requestedAverage === 'hour' ? this.$t('unit.protocols.hourly') : this.$t('unit.protocols.daily');

      this.fileName = `${this.unit.name}${average}${valuesString} - ${this.dateFromObject.toLocaleDateString()} - ${this.dateToObject.toLocaleDateString()}`;
    },
    async sendExportRequest() {

      const payload = {
        unitSerialNumber: this.sn,
        from: this.dateFrom,
        to: this.dateTo,
        metrics: this.requestedMetrics.filter((metric) => metric.value).map((metric) => metric.key),
        interval: this.requestedAverage,
      };

      try {
        this.loading = true;
        const response = await fetch(`${VITE_GRAPHQL_REST_URL}/protocol/export`, {
          method: 'POST',
          headers: createHeaders(),
          body: JSON.stringify(payload),
        });

        const data = await response.text();

        this.downloadCSV(data);
      } catch (error) {
        console.log(error);
        console.error(error);
        console.dir(error);
        this.toastStore.showError(this.$t('unit.protocols.exportError'));

      } finally {
        this.loading = false;
      }

    },
    downloadCSV(data) {
      if (!data) {
        return;
      }
      const blob = new Blob([data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${this.fileName}.csv`;
      link.click();
      // document.body.removeChild(link);
    },
  },
};
</script>

<style lang="scss" scoped>
.button-grid {
  display: flex;
  justify-items: stretch;
  gap: .25rem;

  .btn {
    margin: 0;
    font-size: 0.8125rem;
    //flex: 1 1;
    flex: auto;
    padding: .75rem .25rem;
  }
}

.disabled {
  opacity: 0.5;
  pointer-events: none;
}

@media (min-width: 22.5rem) {
  .button-grid {
    .btn {
      flex: 1 1;
    }
  }
}
</style>
